import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { useAuth } from "../../contexts/authContext";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const appHomePath = user?.role === "blogEditor" ? "/blog-admin" : "/dashboard";
  const appHomeLabel = user?.role === "blogEditor" ? "Go to Blog Admin" : "Go to Dashboard";
  const MotionNav = m.nav;
  const MotionButton = m.button;
  const MotionDiv = m.div;

  return (
    <MotionNav
      initial={{ y: 0, opacity: 1 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="flex flex-col md:flex-row items-center justify-between py-4 px-6 mx-auto w-full relative top-0 left-0 right-0 z-50 bg-transparent"
    >
      <div className="flex items-center justify-between w-full lg:w-auto max-w-[150px] xl:max-w-[196px]">
        <div className="text-white font-bold text-2xl tracking-tight flex items-center gap-2">
          <Link to="/">
            <img
              src="/logo.svg"
              alt="Pingly Logo"
              className="nav-logo"
            />
          </Link>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden text-white p-2 rounded-lg bg-orange-500/20 backdrop-blur-sm border border-orange-400/30"
        >
          {isMobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>
      <div className="hidden lg:flex items-center gap-8 lg:gap-[37px]">
        {/* Nav Links */}
        <div className="hidden lg:flex items-center gap-8 lg:gap-[67px]">
          {[
            { label: "Home", to: "/" },
            { label: "About", to: "/about" },
            { label: "Blog", to: "/blog" },
            { label: "FAQ", to: "/faq" },
            { label: "Contact", to: "/contact-us" },
          ].map((link) => (
            <NavLink
              key={link.label}
              to={link.to}
              end={link.to === "/"} // important for Home
              className={({ isActive }) =>
                `text-white text-[16px] md:text-[18px] lg:text-[20px] font-normal leading-[150%] transition-all duration-200 ${isActive ? "opacity-100" : "opacity-60 hover:opacity-100"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3 lg:gap-[14px]">
          {isAuthenticated ? (
            <Link to={appHomePath}>
              <MotionButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="cursor-pointer bg-[#FF974D] text-[#fff] px-[32px] py-[14px] rounded-[45px] text-[16px] lg:text-[18px] font-semibold text-center transition-all hover:bg-[#F87B0F]"
              >
                {appHomeLabel}
              </MotionButton>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <MotionButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="cursor-pointer bg-[#FFF5F3] text-[#09042C] px-[32px] py-[14px] rounded-[45px] text-[16px] lg:text-[18px] font-semibold text-center transition-all hover:bg-[#ffe4df]"
                >
                  Start Free Trial
                </MotionButton>
              </Link>
              <Link to="/login">
                <MotionButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="cursor-pointer bg-[#FF974D] text-[#fff] px-[32px] py-[14px] rounded-[45px] text-[16px] lg:text-[18px] font-semibold text-center transition-all hover:bg-[#F87B0F]"
                >
                  Book a Demo
                </MotionButton>
              </Link>
            </>
          )}
        </div>
      </div>
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MotionDiv
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden absolute top-full left-4 right-4 mt-2 bg-white/95 backdrop-blur-md rounded-2xl p-6 flex flex-col gap-5 shadow-2xl border border-gray-100/50 z-50"
          >
            <div className="flex flex-col gap-4">
              {[
                { label: "Home", to: "/" },
                { label: "About", to: "/about" },
                { label: "Blog", to: "/blog" },
                // { label: "Pricing", to: "#" },
                { label: "FAQ", to: "/faq" },
                { label: "Contact", to: "/contact-us" },
              ].map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-800 hover:text-orange-500 text-[16px] lg:text-[18px] font-semibold transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="h-[1px] w-full bg-gray-200/50 my-1" />

            <div className="flex flex-col gap-3">
              {isAuthenticated ? (
                <Link to={appHomePath} onClick={() => setIsMobileMenuOpen(false)}>
                  <button
                    type="button"
                    className="w-full bg-orange-100 text-orange-600 border border-orange-200 px-6 py-3 rounded-xl text-[16px] font-bold transition-all hover:bg-orange-200"
                  >
                    {appHomeLabel}
                  </button>
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                    <button
                      type="button"
                      className="w-full bg-gray-50 text-gray-700 border border-gray-200 px-6 py-3 rounded-xl text-[16px] font-bold transition-all hover:bg-gray-100"
                    >
                      Start Free Trial
                    </button>
                  </Link>
                  <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                    <button
                      type="button"
                      className="w-full bg-orange-100 text-orange-600 border border-orange-200 px-6 py-3 rounded-xl text-[16px] font-bold transition-all hover:bg-orange-200"
                    >
                      Create a free account
                    </button>
                  </Link>
                </>
              )}
            </div>
          </MotionDiv>
        )}
      </AnimatePresence>
    </MotionNav>
  );
};

export default Navbar;
